// Type-ahead customer picker used in the Billing/Estimation form. Backed by
// /api/customers-master/search. On select, hands the full customer row back to
// the parent so it can prefill billing/contact fields.

import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import api from "../../lib/api";
import { MagnifyingGlass, X, User, IdentificationCard, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { API } from "../../App";

export default function CustomerSearchPicker({
  user,
  value,            // current customer_id (may be empty)
  selectedLabel,    // display label for the currently-linked customer
  onPick,           // (customer) => void   — full customer row from search payload
  onClear,          // () => void           — optional, called when user clears selection
  testId = "billing-customer-picker",
  placeholder = "Search by name / company / mobile / email / GST / CUST-code…",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const ref = useRef(null);

  const fetchResults = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`${API}/customers-master/search`, {
        params: {
          q: term,
          requester_role: user?.role || "",
          requester_user_id: user?.user_id || "",
          limit: 20,
        },
      });
      setResults(r.data || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.user_id]);

  // Debounced fetch
  useEffect(() => {
    const t = setTimeout(() => fetchResults(q), 250);
    return () => clearTimeout(t);
  }, [q, fetchResults]);

  // Click outside → close
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (c) => {
    onPick && onPick(c);
    setQ("");
    setResults([]);
    setOpen(false);
    setHi(-1);
  };

  const clear = () => {
    onClear && onClear();
    setQ("");
    setResults([]);
    setHi(-1);
  };

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      {value && selectedLabel ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 text-sm" data-testid={`${testId}-selected`}>
          <User size={14} weight="fill" className="text-blue-600" />
          <span className="flex-1 font-medium text-blue-900 truncate">{selectedLabel}</span>
          <button type="button" onClick={clear} className="p-0.5 hover:bg-blue-100 rounded" title="Clear" data-testid={`${testId}-clear`}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            placeholder={placeholder}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { setHi((h) => Math.min(h + 1, results.length - 1)); e.preventDefault(); }
              else if (e.key === "ArrowUp") { setHi((h) => Math.max(h - 1, -1)); e.preventDefault(); }
              else if (e.key === "Enter" && hi >= 0 && results[hi]) { pick(results[hi]); e.preventDefault(); }
              else if (e.key === "Escape") { setOpen(false); }
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7] bg-white"
            data-testid={`${testId}-input`}
          />
        </div>
      )}
      {open && !value && q && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 shadow-lg max-h-72 overflow-y-auto" data-testid={`${testId}-results`}>
          {loading ? (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">No matches. Continue typing in the customer fields below to auto-create a new master record.</div>
          ) : (
            results.map((c, i) => (
              <button
                type="button"
                key={c.customer_id}
                onClick={() => pick(c)}
                onMouseEnter={() => setHi(i)}
                className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 ${hi === i ? "bg-blue-50" : "hover:bg-slate-50"}`}
                data-testid={`${testId}-result-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm text-slate-900 truncate">{c.legal_name || c.display_name}</div>
                  <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.5 text-slate-600">{c.customer_code}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-600 mt-0.5">
                  {c.gstin && <span className="inline-flex items-center gap-1"><IdentificationCard size={10} />{c.gstin}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                  {c.email && <span className="inline-flex items-center gap-1 truncate"><EnvelopeSimple size={10} />{c.email}</span>}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
